/**
 * Centralized Error Handling Hook
 *
 * This hook provides consistent error handling across the application
 * using the error mapping system from otpErrorMapping.ts
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { mapOtpError, extractErrorCode, UITreatment } from '@/src/services/otpErrorMapping'

interface ErrorState {
    message: string
    uiTreatment?: UITreatment
    field?: 'email' | 'mobile' | 'countryCode' | 'otp'
}

interface UseErrorHandlerOptions {
    /**
     * Optional translation function for error messages
     */
    translate?: (key: string) => string
}

export function useErrorHandler(options: UseErrorHandlerOptions = {}) {
    const { translate = (key: string) => key } = options

    // Use ref to avoid dependency changes
    const translateRef = useRef(translate)

    // Update ref when translate changes
    useEffect(() => {
        translateRef.current = translate
    }, [translate])

    const [error, setError] = useState('')
    const [errorState, setErrorState] = useState<ErrorState>({ message: '' })

    /**
     * Clears all error state
     */
    const clearError = useCallback(() => {
        setError('')
        setErrorState({ message: '' })
    }, [])

    /**
     * Sets a simple error message
     */
    const setSimpleError = useCallback((message: string) => {
        setError(message)
        setErrorState({ message, uiTreatment: UITreatment.GENERAL_MODAL_ERROR })
    }, [])

    /**
     * Handles API errors using the error mapping system
     * This is the primary error handler for API calls
     */
    const handleApiError = useCallback(
        (err: any, fallbackMessage: string) => {
            const errorCode = extractErrorCode(err)
            const errorMapping = mapOtpError(errorCode, translateRef.current, fallbackMessage)
            setError(errorMapping.message)
            setErrorState(errorMapping)
            return errorMapping
        },
        []
    )

    /**
     * Handles OTP verification errors
     * Uses translated fallback message
     */
    const handleOtpError = useCallback((err: any) => {
        const errorCode = extractErrorCode(err)
        const fallbackMessage = translateRef.current('invalidOtpTryAgain')
        const errorMapping = mapOtpError(errorCode, translateRef.current, fallbackMessage)
        setError(errorMapping.message)
        setErrorState(errorMapping)
        return errorMapping
    }, [])

    /**
     * Handles validation errors
     * Sets error with inline validation treatment
     */
    const handleValidationError = useCallback((message: string, field?: ErrorState['field']) => {
        setError(message)
        setErrorState({
            message,
            uiTreatment: UITreatment.INLINE_VALIDATION,
            field
        })
    }, [])

    return {
        // Error state
        error,
        errorState,

        // Error setters
        setError: setSimpleError,
        clearError,

        // Error handlers
        handleApiError,
        handleOtpError,
        handleValidationError
    }
}
